/**
 * Build-time configuration for the browser bundle.
 *
 * ⚠️ **This file exists because `src/data/plans.ts` cannot hold it.** The Worker imports
 * `plans.ts` (see `worker/knowledge.ts`, which grounds the assistant in the real tiers), and a
 * Worker gets no Vite env injection — `import.meta.env` is simply absent there. A top-level
 * `import.meta.env.X` read in a module the Worker imports is evaluated the moment that module
 * loads, so it would not merely fail to resolve: it would **throw at Worker startup**, taking
 * `/api/chat` and `/api/audit-request` down with it. `tsc -p worker/tsconfig.json` caught it as
 * a type error, which is a narrower complaint than the actual hazard.
 *
 * The rule this encodes: anything read from `import.meta.env` belongs in a module the Worker
 * never imports. Keep that boundary rather than reaching for a `vite/client` reference, which
 * would silence the type error and leave the crash.
 */

/**
 * Where the app lives.
 *
 * ⚠️ **This pointed at `my.dedupr.cloud/#/local` and that route no longer exists.** The
 * 2026-08-06 reshape made local mode the installed PWA rather than a URL, and `#/local` was later
 * removed from the router's allow-list entirely — so every one of the three marketing surfaces
 * linking here was sending visitors to a hash that resolves to the dashboard or the sign-in card.
 * The landing app was not audited when that route was deleted, which is the whole hazard of a URL
 * owned in one repo and consumed in another.
 *
 * The honest destination is the app's front door: local mode is reached by installing from there,
 * and a visitor with no account meets sign-up rather than a dead hash.
 *
 * One owner, because three surfaces link to it: the Modes card, the pricing cards, and the
 * `#/pricing` page's local-mode section. Two of those were still opening a lead-capture modal
 * after the first was fixed, which is how a marketing page ends up asking for an email address
 * to reach the one thing that needs no account at all.
 */
export const PWA_URL = import.meta.env.VITE_PWA_URL ?? 'https://my.dedupr.cloud/';

/**
 * Where somebody starts a real trial — the product's own gate, which already asks for exactly what
 * the audit-request modal asks for (work email, company, what they are trying to sort out) and
 * additionally *creates something*: an organisation, a 7-day trial, an Entry/Sandbox tier.
 */
export const SIGNUP_URL = import.meta.env.VITE_SIGNUP_URL ?? 'https://my.dedupr.cloud/';
